/** One honest line about the lock state of the window a figure is for — **ADR-0094 D2**.
 *
 * Every response that totals carries `window_lock`, so this renders from what the server said and
 * from nothing else. It says *closed* or *includes a closed period* and never "final": a closed
 * period still accepts an Admin's reasoned change, and the product's own filing verdict never says
 * ready on its own.
 *
 * **Nothing is rendered for `open`, and nothing for a response that carried no lock.** An absent
 * banner is not a claim that the window is open; it is this component declining to say. What is not
 * served here is *reopened since* — `window_lock` has two closed states and no history — so this
 * does not say it; the period panel on Reports does, from the period's own lock.
 */

import { shortDate } from "../lib/format";
import type { WindowLock } from "../api/periodLock";
import styles from "./WindowLockBanner.module.css";

export function WindowLockBanner({ lock }: { lock: WindowLock | null | undefined }) {
  if (!lock || lock.state === "open") return null;
  const names = lock.closed
    .map((p) => `${p.regime_key.replace(/_/g, " ")} ${p.label}${p.closed_at ? `, closed ${shortDate(p.closed_at)}` : ""}`)
    .join("; ");
  return (
    <p className={styles.banner} role="note" data-testid="window-lock" data-state={lock.state}>
      {lock.state === "closed" ? (
        <>
          <strong>Closed.</strong> {names ? `${names}. ` : ""}Changes now need an Admin and a reason.
        </>
      ) : (
        <>
          <strong>Includes a closed period.</strong> {names ? `${names}. ` : ""}Changes to that part
          need an Admin and a reason; the rest of this window can still change.
        </>
      )}
    </p>
  );
}
